// shellBrand.mjs — 左上角标识的判据（第一百一十批 B ⑥，需求方指定形态）。
//
// 需求方要的是：**🚦（横屏）/ 🚥（纵屏）**，点击或 hover 展开 tips 显示
// "Dev Coordinator"，**再次互动或超时自动隐藏**。
//
// ── 为什么用 `(orientation: portrait)` 而不是 MUI 断点 ───────────────────────
// 现在这里按 **xs/sm 断点**在 "DC" 与 "Dev Coordinator" 之间切，而断点是**视口宽度**。
// 宽度与方向不是一件事，两处实例足以说明：
//   · iPad 竖屏 768×1024 —— MUI 判 `sm`（≥600），而它是**纵屏**；
//   · 手机横屏 844×390  —— MUI 判 `sm`，它是**横屏**；两者会拿到同一个断点、
//     却要拿到两个不同的字形。
// CSS 的 `orientation` media query 定义就是"视口宽 ≥ 高 为 landscape，否则 portrait"
// ——也就是需求方说的那个宽高比，只是已经写成了标准、由浏览器算、随窗口变化实时
// 更新。自己拿 `innerWidth/innerHeight` 再算一遍等于把一条现成的标准抄成一份会漂的
// 副本（且要自己处理 resize 订阅与移动端地址栏引起的高度抖动）。
//
// 桌面窄高窗口会判成 portrait —— 这是**对的**，不是缺陷：这个字形讲的是"视口是横的
// 还是竖的"，一个 500×900 的浏览器窗口确实是竖的。
//
// ── tips 的状态机为什么在这里 ──────────────────────────────────────────────
// 「再次互动或超时自动隐藏」是一条**可判定**的规则（SB-042：可判定逻辑入 .mjs 配
// 单测；`.jsx` 里不放判据——上一批为此付过账）。写成 `useState` 里的三个内联三元
// 表达式，它就永远不进单测，而这条规则恰恰有两个容易写反的地方：点击时**收起**
// （不是再打开一次）、以及超时只关不开。

// 横屏 🚦（三灯横排）/ 纵屏 🚥（三灯纵排）。字形本身就是方向的隐喻，
// 所以它同时是标识、也是"你现在是横是竖"的一枚小提示。
export const BRAND_GLYPH_LANDSCAPE = '🚦';
export const BRAND_GLYPH_PORTRAIT = '🚥';

export const brandGlyph = (portrait) =>
  (portrait ? BRAND_GLYPH_PORTRAIT : BRAND_GLYPH_LANDSCAPE);

// 自动隐藏的时限。3.5s ≈ 读完 "Dev Coordinator" 两遍——再长就成了一块常驻遮挡，
// 而它遮的正是工具条最左边那格（xs 断点下汉堡就在旁边）。
export const BRAND_TIP_TIMEOUT_MS = 3500;

// tips 的状态机。事件五种，每一种都有一条具体的理由：
//   'enter'   指针悬停/键盘聚焦 ⇒ 展开（hover 那一半）
//   'leave'   指针离开/失焦     ⇒ 收起（悬停的自然结束）
//   'toggle'  点击/回车         ⇒ **翻转**（"再次互动自动隐藏"的那一半：
//                                 已经开着时再点一下是收起，不是再打开一次）
//   'timeout' 计时器到点        ⇒ 只关不开
//   'route'   路由变了          ⇒ 收起（同 AppShell 里那条"路由一变就收屉"：
//                                 浏览器前进/后退不经过任何 leave 事件，
//                                 一个跟着换页留在原地的气泡看起来像卡住了）
// 未知事件保持原样：一个"没定义的动作把面板关了"比什么都不做更难查。
export function brandTipNext(open, event) {
  switch (event) {
    case 'enter': return true;
    case 'leave': return false;
    case 'toggle': return !open;
    case 'timeout': return false;
    case 'route': return false;
    default: return Boolean(open);
  }
}

// 计时器该不该挂。只在**开着**的时候挂——关着的时候挂一个"到点后关闭"的计时器，
// 会在下一次展开后不到时限就把它关掉（计时器是按上一次展开起算的）。
export const brandTipNeedsTimer = (open) => Boolean(open);
